import useLocale from "@hooks/useLocale"
import computeSlug from "@utils/computeSlug"

export interface Resource {
  id: string
  name?: string
  globalId?: string
  username?: string
}

export type LinkPage =
  | "index"
  | "recipes"
  | "culture"
  | "about"
  | "faq"
  | "support"
  | "follow"
  | "stories"
  | "users"
  | "brands"
  | "bundles"
  | "collections"

export interface UseLinkPayload {
  page?: LinkPage
  recipe?: any
  story?: any
  user?: any
  brand?: any
  bundle?: any
  collection?: any
  href?: string | null
}

export interface LinkHook {
  href: string
}

function getHrefForPage(page: LinkPage) {
  switch (page) {
    case "index":
      return "/"
    case "recipes":
      return "/r"
    case "users":
      return "/profile"
    case "stories":
      return "/s"
    case "brands":
      return "/b"
    case "collections":
      return "/c"
    case "bundles":
      return "/b"
    default:
      return "/" + page
  }
}

const useLink = (payload: UseLinkPayload): LinkHook => {
  const { page, recipe, bundle, story, brand, user, collection } = payload
  const { lang } = useLocale()

  if (page) {
    return {
      href: getHrefForPage(page),
    }
  }

  if (recipe) {
    return {
      href: `/r/${recipe.id}/${computeSlug(recipe.name, true)}`,
    }
  }

  if (bundle) {
    return {
      href: `/b/${bundle.id}/${computeSlug(bundle.name, true)}`,
    }
  }

  if (story) {
    return {
      href: "/s/" + story.id + "/" + computeSlug(story.name[lang], true),
    }
  }

  if (brand) {
    return {
      href: "/" + computeSlug(brand.name[lang], true),
    }
  }

  if (user) {
    if (user.slug) {
      return {
        href: `/u/${user.slug}`,
      }
    }

    return {
      href: `/profile/${user.id}`,
    }
  }

  if (collection) {
    return {
      href: `/c/${collection.id}/${computeSlug(collection.name, true)}`,
    }
  }

  return {
    href: payload.href ?? "/",
  }
}

export default useLink
